/**
 * Frame component registry.
 *
 * Maps frame types to their components, each inside the shared,
 * renderer-neutral frame chrome. Surfaces render these directly (any plain
 * container) or adapt them (the canvas `nodeTypes`).
 *
 * 30 frame types; people create Docs and task boards, agents the rest.
 * `note` and `markdown`, the Doc's former types, render as the Doc.
 *
 * Heavy frame types are lazy-loaded so their dependencies do not block the
 * first render when no matching frames are visible.
 */

import { lazy, type ComponentType } from "react";
import type { FrameType } from "@/api/spaces";
import type { FrameProps } from "./shared/frame-props";
import { withFrameChrome } from "./shared/with-frame-chrome";
import { DocFrame } from "./doc-frame/doc-frame";
import { HtmlFrame } from "./html-frame";
import { CodeFrame } from "./code-frame";
import { SearchFrame } from "./search-frame";
import { ImageFrame } from "./image-frame";
import { EmailFrame } from "./email-frame";
import { ConnectionFrame } from "./connection-frame";
import { ConnectionPromptFrame } from "./connection-prompt-frame";
import { TaskboardFrame } from "./taskboard-frame";
import { GithubFrame } from "./github-frame";
import { EventFrame } from "./event-frame";
import { AgendaFrame } from "./agenda-frame";
import { FlightFrame } from "./flight-frame";
import { WeatherFrame } from "./weather-frame";
import { ChartFrame } from "./chart-frame";
import { DatasetFrame } from "./dataset-frame";
import { PreviewCardFrame } from "./preview-card-frame";
import { LinkPreviewFrame } from "./link-preview-frame";
import { ChatFrame } from "./chat-frame";
import { SlackFrame } from "./slack-frame";
import { TodoFrame } from "./todo-frame";
import { BoardFrame } from "./board-frame";
import { MusicFrame } from "./music-frame";
import { CalendarFrame } from "./calendar-frame";
import { FileFrame } from "./file-frame";

const MemoryFrame = lazy(() =>
  import("./memory-frame").then((m) => ({ default: m.MemoryFrame })),
);
const ConversationFrame = lazy(() =>
  import("./conversation-frame").then((m) => ({
    default: m.ConversationFrame,
  })),
);
const TerminalFrame = lazy(() =>
  import("./terminal-frame").then((m) => ({ default: m.TerminalFrame })),
);

const docFrame = withFrameChrome(DocFrame);

export const frameComponents = {
  markdown: docFrame,
  html: withFrameChrome(HtmlFrame),
  code: withFrameChrome(CodeFrame),
  search: withFrameChrome(SearchFrame),
  image: withFrameChrome(ImageFrame),
  email: withFrameChrome(EmailFrame),
  connection: withFrameChrome(ConnectionFrame),
  connection_prompt: withFrameChrome(ConnectionPromptFrame),
  memory: withFrameChrome(MemoryFrame),
  note: docFrame,
  doc: docFrame,
  conversation: withFrameChrome(ConversationFrame),
  taskboard: withFrameChrome(TaskboardFrame),
  github: withFrameChrome(GithubFrame),
  event: withFrameChrome(EventFrame),
  agenda: withFrameChrome(AgendaFrame),
  flight: withFrameChrome(FlightFrame),
  weather: withFrameChrome(WeatherFrame),
  chart: withFrameChrome(ChartFrame),
  dataset: withFrameChrome(DatasetFrame),
  preview_card: withFrameChrome(PreviewCardFrame),
  link_preview: withFrameChrome(LinkPreviewFrame),
  chat: withFrameChrome(ChatFrame),
  slack: withFrameChrome(SlackFrame),
  todo: withFrameChrome(TodoFrame),
  board: withFrameChrome(BoardFrame),
  music: withFrameChrome(MusicFrame),
  calendar: withFrameChrome(CalendarFrame),
  file: withFrameChrome(FileFrame),
  terminal: withFrameChrome(TerminalFrame),
} satisfies Partial<Record<FrameType, ComponentType<FrameProps>>>;
